import React, { useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { QUIZ_T } from "@/lib/quizTranslations";
import {
  QUIZ_QUESTIONS,
  buildQuizSet,
  levelFromScore,
  LEVEL_DESCRIPTIONS,
} from "@/lib/quizQuestions";
import { Input } from "@/components/ui/input";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Sparkles,
  RotateCcw,
  Loader2,
  Lock,
  Play,
  Pause,
} from "lucide-react";
import { BRAND, whatsappLink } from "@/lib/config";
import { useCanonical } from "@/lib/useCanonical";
import { usePageTitle } from "@/lib/usePageTitle";
import FluencyCalculator from "@/components/landing/FluencyCalculator";
import QuizSocialShare from "@/components/landing/QuizSocialShare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_INTRO = "intro";
const STAGE_QUIZ = "quiz";
const STAGE_GATE = "gate";
const STAGE_RESULT = "result";

export default function LevelTest() {
  const { lang } = useLanguage();
  const t = QUIZ_T[lang] || QUIZ_T.pt;
  useCanonical("/teste-de-nivel");
  usePageTitle("Teste de nível de francês grátis · A1 · A2 · B1 · B2 · C1 · C2");

  const [stage, setStage] = useState(STAGE_INTRO);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questions, setQuestions] = useState(() => buildQuizSet(4));

  const total = questions.length;
  const question = questions[current];
  const selected = answers[question?.id];

  const score = useMemo(
    () =>
      questions.reduce(
        (acc, q) => acc + (answers[q.id] === q.correctIndex ? 1 : 0),
        0
      ),
    [answers, questions]
  );
  const level = levelFromScore(score, total);

  const reset = () => {
    setStage(STAGE_INTRO);
    setCurrent(0);
    setAnswers({});
    setQuestions(buildQuizSet(4));
  };

  return (
    <div
      data-testid="level-test-page"
      className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a]"
    >
      <header className="border-b border-[#e5e3db]">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="quiz-home-link"
            className="flex items-center gap-3 group"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#1e2a5a] text-[#1e2a5a] font-display text-base italic">
              L
            </span>
            <span className="font-display text-sm sm:text-base">
              {BRAND.name}
            </span>
          </Link>
          <Link
            to="/"
            data-testid="quiz-back-link"
            className="text-xs sm:text-sm link-underline text-[#1e2a5a]"
          >
            {t.intro.back}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-12 py-16 sm:py-24">
        {stage === STAGE_INTRO && (
          <IntroStage t={t} onStart={() => setStage(STAGE_QUIZ)} />
        )}
        {stage === STAGE_QUIZ && question && (
          <QuizStage
            t={t}
            total={total}
            current={current}
            question={question}
            selected={selected}
            onSelect={(i) =>
              setAnswers((a) => ({ ...a, [question.id]: i }))
            }
            onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
            onNext={() =>
              setCurrent((c) => Math.min(total - 1, c + 1))
            }
            onFinish={() => setStage(STAGE_GATE)}
          />
        )}
        {stage === STAGE_GATE && (
          <EmailGateStage
            t={t}
            lang={lang}
            level={level}
            score={score}
            total={total}
            onUnlock={() => setStage(STAGE_RESULT)}
          />
        )}
        {stage === STAGE_RESULT && (
          <ResultStage
            t={t}
            lang={lang}
            score={score}
            total={total}
            level={level}
            questions={questions}
            answers={answers}
            onRetake={reset}
          />
        )}
      </main>

      <LevelTestSEO lang={lang} />
    </div>
  );
}

const SEO_COPY = {
  pt: {
    h2: "Como funciona o teste de nível de francês",
    intro:
      "Nosso teste de nível CEFR foi elaborado pelo Professor Lionel, parisiense da gema com 20 anos de experiência ensinando francês para brasileiros no Rio de Janeiro. Em apenas 5 minutos, você responde 20 questões reais que cobrem gramática, vocabulário, compreensão escrita e estruturas idiomáticas dos níveis A1 ao C1 do Quadro Comum Europeu de Referência (CEFR).",
    h3a: "O que você descobre ao terminar",
    bodyA:
      "Ao final do teste você recebe seu nível CEFR oficial (A1 iniciante, A2 básico, B1 intermediário, B2 avançado ou C1 autônomo), sua pontuação detalhada, a correção comentada de cada questão e uma explicação clara para cada resposta. Você também recebe por e-mail um diagnóstico personalizado com sugestões de próximos passos para evoluir o seu francês.",
    h3b: "Por que fazer com um professor nativo",
    bodyB:
      "Diferente de testes genéricos, este teste foi desenhado por um professor francês nativo que conhece os erros típicos dos brasileiros estudando francês — confusões com gênero, pronomes, passé composé vs imparfait, prepositions, faux amis. As correções vêm com dicas práticas para evitar esses erros no dia a dia, seja para conversação, viagem à França, ou preparação dos exames DELF, DALF, TEF e TCF.",
    h3c: "Sem cadastro complicado, 100% grátis",
    bodyC:
      "Você não precisa criar conta nem instalar nada. Basta começar a responder. Ao final, deixe apenas seu nome e e-mail para receber o resultado completo. Usamos seus dados exclusivamente para enviar o diagnóstico e, se você quiser, conteúdos relevantes sobre francês. Sem spam, sem compartilhamento com terceiros. Consulte nossa política de privacidade para saber mais.",
    h3d: "Depois do teste: 1ª aula grátis",
    bodyD:
      "Identificou seu nível e quer dar o próximo passo? Reserve uma aula gratuita de 50 minutos com o Professor Lionel — 100% online, particular, sem compromisso. Você confirma seu nível ao vivo, ataca seus 3 maiores travamentos e sai com um plano de estudo sob medida para a sua realidade: viagem, prova, conversação ou trabalho.",
  },
  en: {
    h2: "How the French level test works",
    intro:
      "Our CEFR level test was designed by Professor Lionel, a native Parisian with 20 years of experience teaching French to Brazilians in Rio de Janeiro. In just 5 minutes, you'll answer 20 real questions covering grammar, vocabulary, reading comprehension and idiomatic structures across levels A1 to C1 of the Common European Framework of Reference (CEFR).",
    h3a: "What you'll learn at the end",
    bodyA:
      "At the end of the test, you'll receive your official CEFR level (A1 beginner, A2 elementary, B1 intermediate, B2 upper-intermediate or C1 advanced), your detailed score, a commented correction for each question and a clear explanation for every answer. You'll also receive a personalised diagnosis by email with suggested next steps to improve your French.",
    h3b: "Why take it with a native teacher",
    bodyB:
      "Unlike generic tests, this one was built by a native French teacher who knows the typical mistakes Brazilians make studying French — gender confusion, pronouns, passé composé vs imparfait, prepositions, faux amis. Corrections come with practical tips to avoid these mistakes in daily life, whether for conversation, travel to France, or preparation for the DELF, DALF, TEF and TCF exams.",
    h3c: "No complicated signup, 100% free",
    bodyC:
      "You don't need to create an account or install anything. Just start answering. At the end, simply leave your name and email to receive the full result. We use your data exclusively to send the diagnosis and, if you'd like, relevant French content. No spam, no sharing with third parties. See our privacy policy for details.",
    h3d: "After the test: 1st class free",
    bodyD:
      "Found your level and want to take the next step? Book a free 50-minute lesson with Professor Lionel — 100% online, private, no commitment. Confirm your level live, tackle your 3 biggest blockers and leave with a tailored study plan for your reality: travel, exam, conversation or work.",
  },
  fr: {
    h2: "Comment fonctionne le test de niveau de français",
    intro:
      "Notre test de niveau CECR a été conçu par le Professeur Lionel, Parisien d'origine fort de 20 ans d'expérience à enseigner le français aux Brésilien·ne·s à Rio de Janeiro. En seulement 5 minutes, vous répondez à 20 questions réelles couvrant grammaire, vocabulaire, compréhension écrite et structures idiomatiques des niveaux A1 à C1 du Cadre Européen Commun de Référence (CECR).",
    h3a: "Ce que vous découvrez à la fin",
    bodyA:
      "À la fin du test, vous recevez votre niveau CECR officiel (A1 grand débutant, A2 élémentaire, B1 intermédiaire, B2 avancé ou C1 autonome), votre score détaillé, la correction commentée de chaque question et une explication claire pour chaque réponse. Vous recevez aussi par e-mail un diagnostic personnalisé avec des suggestions pour progresser.",
    h3b: "Pourquoi le faire avec un professeur natif",
    bodyB:
      "Contrairement aux tests génériques, celui-ci a été conçu par un professeur français natif qui connaît les erreurs typiques des Brésilien·ne·s — confusions de genre, pronoms, passé composé vs imparfait, prépositions, faux amis. Les corrections incluent des astuces pratiques pour éviter ces erreurs au quotidien, que ce soit pour la conversation, un voyage en France, ou la préparation du DELF, DALF, TEF et TCF.",
    h3c: "Sans inscription complexe, 100 % offert",
    bodyC:
      "Vous n'avez pas besoin de créer un compte ni d'installer quoi que ce soit. Commencez simplement à répondre. À la fin, laissez juste votre nom et e-mail pour recevoir le résultat complet. Nous utilisons vos données uniquement pour envoyer le diagnostic et, si vous le souhaitez, des contenus pertinents sur le français. Aucun spam, aucun partage avec des tiers.",
    h3d: "Après le test : 1er cours offert",
    bodyD:
      "Vous avez trouvé votre niveau et voulez passer à l'étape suivante ? Réservez un cours offert de 50 minutes avec le Professeur Lionel — 100 % en ligne, particulier, sans engagement. Vous confirmez votre niveau en direct, attaquez vos 3 blocages principaux et repartez avec un plan d'étude sur mesure pour votre réalité : voyage, examen, conversation ou travail.",
  },
};

const LevelTestSEO = ({ lang }) => {
  const c = SEO_COPY[lang] || SEO_COPY.pt;
  return (
    <section
      data-testid="level-test-seo"
      aria-label={c.h2}
      className="border-t border-[#e5e3db] bg-white"
    >
      <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-12 py-16 sm:py-24">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1e2a5a] leading-tight">
          {c.h2}
        </h2>
        <p className="mt-6 text-base text-[#3a3a3a] leading-relaxed">{c.intro}</p>

        <h3 className="font-display mt-12 text-xl sm:text-2xl text-[#1e2a5a]">
          {c.h3a}
        </h3>
        <p className="mt-4 text-base text-[#3a3a3a] leading-relaxed">{c.bodyA}</p>

        <h3 className="font-display mt-10 text-xl sm:text-2xl text-[#1e2a5a]">
          {c.h3b}
        </h3>
        <p className="mt-4 text-base text-[#3a3a3a] leading-relaxed">{c.bodyB}</p>

        <h3 className="font-display mt-10 text-xl sm:text-2xl text-[#1e2a5a]">
          {c.h3c}
        </h3>
        <p className="mt-4 text-base text-[#3a3a3a] leading-relaxed">{c.bodyC}</p>

        <h3 className="font-display mt-10 text-xl sm:text-2xl text-[#1e2a5a]">
          {c.h3d}
        </h3>
        <p className="mt-4 text-base text-[#3a3a3a] leading-relaxed">{c.bodyD}</p>
      </div>
    </section>
  );
};

const IntroStage = ({ t, onStart }) => (
  <div data-testid="quiz-intro">
    <p className="overline">{t.intro.overline}</p>
    <h1 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
      {t.intro.title}
    </h1>
    <p className="mt-6 text-base sm:text-lg text-[#5c5c5c] leading-relaxed max-w-xl">
      {t.intro.subtitle}
    </p>
    <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
      {t.intro.meta.map((m, i) => (
        <li key={i} className="flex items-center gap-2">
          <span className="h-1 w-1 rounded-full bg-[#d9381e]" />
          {m}
        </li>
      ))}
    </ul>
    <button
      onClick={onStart}
      data-testid="quiz-start-button"
      className="group mt-12 inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e]"
    >
      <Sparkles className="h-4 w-4" />
      {t.intro.cta}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </button>
  </div>
);

const QuizStage = ({
  t,
  total,
  current,
  question,
  selected,
  onSelect,
  onPrev,
  onNext,
  onFinish,
}) => {
  const isLast = current === total - 1;
  const progress = ((current + 1) / total) * 100;

  return (
    <div data-testid="quiz-stage">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-[#5c5c5c]">
        <span>
          {t.quiz.question} {current + 1} {t.quiz.of} {total}
        </span>
        <span className="font-medium text-[#d9381e]">{question.level}</span>
      </div>
      <div className="mt-3 h-1 w-full bg-[#e5e3db] overflow-hidden rounded-full">
        <div
          className="h-full bg-[#1e2a5a] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      <h2
        data-testid={`quiz-question-${question.id}`}
        className="font-display mt-10 text-2xl sm:text-3xl tracking-tight text-[#1e2a5a] leading-snug"
      >
        {question.question}
      </h2>

      <ul className="mt-8 space-y-3">
        {question.options.map((opt, i) => {
          const active = selected === i;
          return (
            <li key={i}>
              <button
                onClick={() => onSelect(i)}
                data-testid={`quiz-option-${question.id}-${i}`}
                className={`w-full text-left flex items-start gap-4 rounded-xl border px-5 py-4 transition-all ${
                  active
                    ? "border-[#1e2a5a] bg-white shadow-sm"
                    : "border-[#e5e3db] bg-white/60 hover:border-[#1e2a5a]/60"
                }`}
              >
                <span
                  className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium ${
                    active
                      ? "bg-[#1e2a5a] border-[#1e2a5a] text-[#f4f1ea]"
                      : "border-[#5c5c5c] text-[#5c5c5c]"
                  }`}
                >
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="text-base text-[#1e2a5a]">{opt}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-10 flex items-center justify-between gap-3">
        <button
          onClick={onPrev}
          disabled={current === 0}
          data-testid="quiz-prev"
          className="inline-flex items-center gap-2 rounded-full border border-[#1e2a5a] px-5 py-2.5 text-sm text-[#1e2a5a] transition-colors hover:bg-[#1e2a5a] hover:text-[#f4f1ea] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#1e2a5a]"
        >
          <ArrowLeft className="h-4 w-4" /> {t.quiz.prev}
        </button>
        {isLast ? (
          <button
            onClick={onFinish}
            disabled={selected === undefined}
            data-testid="quiz-finish"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-2.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t.quiz.finish}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        ) : (
          <button
            onClick={onNext}
            disabled={selected === undefined}
            data-testid="quiz-next"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-2.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t.quiz.next}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        )}
      </div>
      {selected === undefined && (
        <p className="mt-4 text-xs text-[#d9381e]">{t.quiz.pickOne}</p>
      )}
    </div>
  );
};

const EmailGateStage = ({ t, lang, level, score, total, onUnlock }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quiz-results`, {
        name: name.trim(),
        email: email.trim(),
        level,
        score,
        total,
        language: lang,
      });
      onUnlock();
    } catch (err) {
      console.error(err);
      toast.error(t.gate.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quiz-gate">
      <p className="overline">{t.gate.overline}</p>
      <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1e2a5a] leading-[1.1]">
        {t.gate.title}
      </h2>
      <p className="mt-6 text-base sm:text-lg text-[#5c5c5c] leading-relaxed max-w-xl">
        {t.gate.subtitle}
      </p>

      <form onSubmit={submit} className="mt-10 max-w-md space-y-5">
        <label htmlFor="gate-name" className="flex flex-col gap-2">
          <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-medium">
            {t.gate.name}
          </span>
          <Input
            id="gate-name"
            data-testid="gate-name-input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
          />
        </label>
        <label htmlFor="gate-email" className="flex flex-col gap-2">
          <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-medium">
            {t.gate.email}
          </span>
          <Input
            id="gate-email"
            type="email"
            data-testid="gate-email-input"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
          />
        </label>

        <button
          type="submit"
          data-testid="gate-submit"
          disabled={submitting}
          className="group inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t.gate.submitting}
            </>
          ) : (
            <>
              <Lock className="h-4 w-4" />
              {t.gate.submit}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </button>

        <p className="text-xs text-[#5c5c5c] leading-relaxed pt-2">
          {t.gate.privacy}
        </p>
      </form>
    </div>
  );
};

const VOICE_NOTE = {
  pt: { label: "Um recado do Prof Lionel", play: "Ouvir (12 s)", pause: "Pausar" },
  fr: { label: "Un message du Prof Lionel", play: "Écouter (12 s)", pause: "Pause" },
  en: { label: "A note from Prof Lionel", play: "Listen (12 s)", pause: "Pause" },
};

const ResultVoiceNote = ({ lang }) => {
  const key = VOICE_NOTE[lang] ? lang : "pt";
  const copy = VOICE_NOTE[key];
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    if (playing) ref.current.pause();
    else ref.current.play();
  };
  return (
    <div className="mt-10 flex items-center gap-4 rounded-2xl border border-[#e5e3db] bg-white p-4" data-testid="quiz-result-voice-note">
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? copy.pause : copy.play}
        data-testid="quiz-result-voice-play"
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#d9381e] text-white transition-transform hover:scale-105"
      >
        {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-px" />}
      </button>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#d9381e]">{copy.label}</p>
        <p className="text-sm text-[#5c5c5c]">{playing ? copy.pause : copy.play}</p>
      </div>
      <audio
        ref={ref}
        src={`/audio/quiz-result-${key}.mp3`}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
};

const ResultStage = ({ t, lang, score, total, level, questions, answers, onRetake }) => {
  const description = LEVEL_DESCRIPTIONS[level][lang] || LEVEL_DESCRIPTIONS[level].fr;

  return (
    <div data-testid="quiz-result">
      <p className="overline">{t.result.overline}</p>
      <div className="mt-6 grid sm:grid-cols-2 gap-8 items-end">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5c5c5c]">
            {t.result.yourLevel}
          </p>
          <p
            data-testid="quiz-result-level"
            className="font-display text-[120px] leading-none text-[#1e2a5a]"
          >
            {level}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-xs uppercase tracking-[0.2em] text-[#5c5c5c]">
            {t.result.score}
          </p>
          <p className="font-display text-4xl text-[#1e2a5a]">
            {score} / {total}
          </p>
          <p className="mt-4 text-base text-[#5c5c5c] leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      <ResultVoiceNote lang={lang} />

      <div className="mt-12 flex flex-col sm:flex-row gap-3">
        <a
          href="/#contact"
          data-testid="quiz-result-cta-book"
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e]"
        >
          {t.result.ctaBook}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </a>
        <button
          onClick={onRetake}
          data-testid="quiz-result-retake"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1e2a5a] px-6 py-3.5 text-sm font-medium text-[#1e2a5a] transition-colors hover:bg-[#1e2a5a] hover:text-[#f4f1ea]"
        >
          <RotateCcw className="h-4 w-4" />
          {t.result.ctaRetake}
        </button>
        <a
          href={whatsappLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="quiz-result-whatsapp"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-medium text-white transition-transform hover:scale-[1.02]"
        >
          WhatsApp
        </a>
      </div>

      {/* Fluency calculator — powerful conversion hook: shows how many months
          to reach a target level with current pace vs 2 classes/week with Prof
          Lionel. Anchors a numerical outcome to the free-class CTA. */}
      <FluencyCalculator currentLevel={level} lang={lang} />

      {/* Social share — post-quiz virality: OG card + WhatsApp/Instagram/copy */}
      <QuizSocialShare level={level} lang={lang} />

      <section className="mt-20">
        <h3 className="font-display text-2xl sm:text-3xl tracking-tight text-[#1e2a5a]">
          {t.result.correctionsTitle}
        </h3>
        <ul className="mt-8 space-y-6">
          {questions.map((q, idx) => {
            const userIdx = answers[q.id];
            const correct = userIdx === q.correctIndex;
            return (
              <li
                key={q.id}
                data-testid={`quiz-review-${q.id}`}
                className="border border-[#e5e3db] bg-white p-6 rounded-xl"
              >
                <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em]">
                  <span className="text-[#5c5c5c]">
                    {idx + 1} · {q.level}
                  </span>
                  {correct ? (
                    <span className="inline-flex items-center gap-1.5 text-[#1e7a3e]">
                      <CheckCircle2 className="h-4 w-4" /> ✓
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[#d9381e]">
                      <XCircle className="h-4 w-4" /> ✗
                    </span>
                  )}
                </div>
                <p className="font-display mt-4 text-lg text-[#1e2a5a]">
                  {q.question}
                </p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="bg-[#f0efea] rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c]">
                      {t.result.yourAnswer}
                    </p>
                    <p
                      className={`mt-1 ${
                        correct ? "text-[#1e7a3e]" : "text-[#d9381e]"
                      }`}
                    >
                      {userIdx === undefined ? "—" : q.options[userIdx]}
                    </p>
                  </div>
                  <div className="bg-[#f0efea] rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c]">
                      {t.result.correctAnswer}
                    </p>
                    <p className="mt-1 text-[#1e2a5a] font-medium">
                      {q.options[q.correctIndex]}
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-sm text-[#5c5c5c] leading-relaxed">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#5c5c5c] mr-2">
                    {t.result.explanation}:
                  </span>
                  {q.explanation}
                </p>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
};

// Re-export for tests
export { QUIZ_QUESTIONS };
